import { useEffect, useRef, useState, useCallback } from 'react';
import { locationSocket } from '@/api/client/socket';
import { useActiveShift } from './api/useShifts';
import { haversineMetres } from '@packages/database/utils';
import { platform } from '@/lib/platform';
import { backgroundLocation } from '@/services/location/background-location.service';
import { locationBuffer } from '@/services/location/location-buffer';

// ── Constants ─────────────────────────────────────────────────────────────────

const MOVEMENT_THRESHOLD_METRES = 5;
const HEARTBEAT_INTERVAL_MS = 60_000;

// ── Types ─────────────────────────────────────────────────────────────────────

export type LocationTrackingState =
  | 'idle'
  | 'acquiring'
  | 'tracking'
  | 'declined'
  | 'unavailable'
  | 'error';

// ── Shared emit logic ─────────────────────────────────────────────────────────

function buildPayload(coords: {
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
}) {
  return {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: coords.accuracy ?? undefined,
    heading: coords.heading ?? undefined,
    speed: coords.speed ?? undefined,
  };
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useLocationTracking = () => {
  console.log('USING LOCATION TRACKING');
  const { data: activeShift } = useActiveShift();
  const isShiftActive = activeShift?.status === 'ACTIVE';

  const [state, setState] = useState<LocationTrackingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [distanceMoved, setDistanceMoved] = useState<number>(0);

  const watchIdRef = useRef<number | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPositionRef = useRef<{
    latitude: number;
    longitude: number;
  } | null>(null);

  // ── Emit to socket (shared by both native and browser paths) ────────────

  const emitToSocket = useCallback(
    (
      coords: {
        latitude: number;
        longitude: number;
        accuracy?: number | null;
        heading?: number | null;
        speed?: number | null;
      },
      isHeartbeat: boolean = false,
    ) => {
      console.log(
        '[EmitToSocket] Called with:',
        coords.latitude,
        coords.longitude,
      );
      console.log('[EmitToSocket] Socket connected?', locationSocket.connected);
      console.log('[EmitToSocket] Socket id:', locationSocket.id);
      const last = lastPositionRef.current;
      const moved = last
        ? haversineMetres(
            last.latitude,
            last.longitude,
            coords.latitude,
            coords.longitude,
          )
        : Infinity;

      if (
        !platform.isNative &&
        !isHeartbeat &&
        moved < MOVEMENT_THRESHOLD_METRES
      ) {
        return;
      }

      const payload = buildPayload(coords);

      if (locationSocket.connected) {
        console.log('LOCATION SOCKET CONNECTED - EMITTING LOCATION');
        locationSocket.emit('update_location', payload);
      } else {
        // Offline — buffer for flush on reconnect
        console.log(
          'USER OFFLINE - BUFFERING LOCATION. Buffer size before push:',
          locationBuffer.size,
        );
        locationBuffer.push({
          ...payload,
          timestamp: new Date().toISOString(),
        });
      }

      lastPositionRef.current = {
        latitude: coords.latitude,
        longitude: coords.longitude,
      };
      setLastUpdate(new Date());
      setDistanceMoved(Math.round(moved === Infinity ? 0 : moved));
      setState('tracking');
      setError(null);
    },
    [],
  );

  // ── Stop (shared cleanup) ───────────────────────────────────────────────

  const stopTracking = useCallback(async () => {
    // Native cleanup
    if (platform.isNative) {
      await backgroundLocation.stop();
    }

    // Browser cleanup
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (heartbeatRef.current !== null) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }

    lastPositionRef.current = null;
    setState('idle');
  }, []);

  // ── Start: Native path ──────────────────────────────────────────────────

  const startNativeTracking = useCallback(async () => {
    setState('acquiring');
    setError(null);

    try {
      await backgroundLocation.init();
      // Reset any stuck state from previous attempt
      await backgroundLocation.stop();

      await backgroundLocation.start({
        distanceFilter: MOVEMENT_THRESHOLD_METRES,
        onLocation: (location) => {
          console.log(
            '[Location] NATIVE onLocation:',
            location.coords.latitude,
            location.coords.longitude,
          );
          emitToSocket(
            {
              latitude: location.coords.latitude,
              longitude: location.coords.longitude,
              accuracy: location.coords.accuracy,
              heading: location.coords.heading,
              speed: location.coords.speed,
            },
            false,
          );
        },
      });

      console.log('[Location] Native tracking started successfully');
      setState('tracking');
    } catch (err) {
      console.error('[Location] Native tracking failed:', err);
      console.error('[Location] Error details:', JSON.stringify(err, null, 2));
      setState('error');
      setError(`Failed to start location tracking`);
    }
  }, [emitToSocket]);

  // ── Start: Browser path (existing logic, unchanged) ─────────────────────

  const startBrowserTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setState('unavailable');
      setError('Geolocation not supported by this browser');
      return;
    }

    setState('acquiring');
    setError(null);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => emitToSocket(position.coords, false),
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setState('declined');
            stopTracking();
            break;
          case err.POSITION_UNAVAILABLE:
            setState('error');
            setError('Location signal unavailable');
            break;
          case err.TIMEOUT:
            setState('error');
            setError('Location request timed out');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15_000,
        maximumAge: 5_000,
      },
    );

    // Heartbeat — same as before
    heartbeatRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => emitToSocket(position.coords, true),
        () => {},
        { enableHighAccuracy: true, maximumAge: 10_000 },
      );
    }, HEARTBEAT_INTERVAL_MS);
  }, [emitToSocket, stopTracking]);

  // ── Lifecycle ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isShiftActive) {
      if (platform.isNative) {
        startNativeTracking();
      } else {
        startBrowserTracking();
      }
    } else {
      stopTracking();
      setError(null);
    }
    return () => {
      stopTracking();
    };
  }, [isShiftActive]);

  // When user reconnects flush out pending location updates
  useEffect(() => {
    const handleReconnect = () => {
      const buffered = locationBuffer.flush();
      if (buffered.length === 0) return;

      // Send as batch — server needs a new handler for this
      locationSocket.emit('location_batch', { locations: buffered });
      console.log(`[Location] Flushed ${buffered.length} buffered locations`);
    };

    locationSocket.on('connect', handleReconnect);
    return () => {
      locationSocket.off('connect', handleReconnect);
    };
  }, []);

  return {
    state,
    isTracking: state === 'tracking' || state === 'acquiring',
    locationDeclined: state === 'declined',
    error,
    lastUpdate,
    distanceMoved,
  };
};
